import React, { useState, useCallback, useRef, useLayoutEffect } from "react";
import { string, func, oneOf } from "prop-types";
import classNames from "classnames";
import offset from "../utils/dom/offset";
import { useScrollPosition } from "../utils/dom/useScrollPosition";
import useReducedMotion from "../utils/dom/useReducedMotion.js";
import { ScrollToTopSpeed } from "./scroll-to-top.types";

export function ScrollToTop({
  ariaLabel,
  className,
  onClick,
  onScrollEnd,
  duration = "250",
  testId,
  id,
}) {
  const [visible, setVisible] = useState(false);
  const [isScrollAtBottom, setIsScrollAtBottom] = useState(false);
  const [rightDistance, setRightDistance] = useState({ right: null });

  let scrollToTopRef = useRef();

  /**
   * Calculates the distance between the right edge of the element and
   * its parent wrapper.
   * @param {number} width
   * @param {element} element
   * @returns right margin of element
   */
  const calcRightDistance = useCallback((width, element) => {
    const elementRight =
      width - getElementPosition(element).elementLeft - element.offsetWidth;

    return (
      width -
      scrollToTopRef.current.offsetLeft -
      scrollToTopRef.current.offsetWidth +
      elementRight
    );
  }, []);

  /**
   * Determine when button should be visible or invisible based distance from page top
   * @param {element} element
   * @returns boolean
   */
  const isVisible = useCallback((element, scroll) => {
    const threshold =
      getElementPosition(element).elementTop + window.innerHeight * 1.5;
    return scroll >= threshold ? true : false;
  }, []);

  /**
   * Handle calculations needed to set the visibility and positioning of the scroll top button.
   * @param {number} currPos
   */
  const handleScrollTop = useCallback(
    (currPos) => {
      const scrollPosition = currPos + window.innerHeight;
      const documentWidth = document.body.clientWidth;
      const parentEl = scrollToTopRef.current.parentNode;

      // Get the total length and position of button wrapper.
      const scrollWrapperBottom =
        parentEl.offsetHeight + getElementPosition(parentEl).elementTop;

      isVisible(parentEl, scrollPosition)
        ? setVisible(true)
        : setVisible(false);
      if (scrollWrapperBottom <= scrollPosition) {
        // When scroll is at the bottom change button position value from fixed to absolute,
        // which negates the need to set the right position.
        // Position must change to absolute at page bottom to prevent button from laying over footer.
        setIsScrollAtBottom(true);
        setRightDistance({ right: null });
      } else {
        // remove absolute position from button when leaving page bottom to scroll up.
        setIsScrollAtBottom(false);

        if (!rightDistance.right) {
          // Reapply right value now that button position is fixed.
          setRightDistance({
            right: calcRightDistance(documentWidth, parentEl) + "px",
          });
        }
      }
    },
    [calcRightDistance, isVisible, rightDistance.right]
  );

  /**
   * When the DOM changes, recalculate scroll top button so we're not displayed incorrectly.
   */
  const _onMutate = useCallback(() => {
    setTimeout(() => {
      if (typeof window === "undefined") return false;
      const currPos = window.scrollY;

      handleScrollTop(currPos);
    }, 50);
  }, [handleScrollTop]);

  useLayoutEffect(() => {
    const canObserve = typeof MutationObserver !== "undefined" ? true : false;
    let _observer = null;

    /**
     * Setup a mutation observer to know when the DOM has changed
     * so we can recalculate scroll top button position and visibility.
     */
    const _addMutationObserver = () => {
      const parentEl = scrollToTopRef.current.parentNode;
      _observer = new MutationObserver(_onMutate);
      _observer.observe(parentEl, {
        childList: true,
        attributes: true,
        characterData: true,
        subtree: true,
      });
    };

    if (typeof window === "undefined") return false;

    /**
     * Remove mutation observer.
     */
    const _removeMutationObserver = () => {
      if (_observer) {
        _observer.disconnect();
      }
    };

    if (canObserve) {
      _addMutationObserver();
    }

    return () => {
      if (canObserve) {
        _removeMutationObserver();
      }
    };
  }, [visible, isScrollAtBottom, rightDistance, _onMutate]);

  useScrollPosition(
    ({ currPos }) => {
      handleScrollTop(currPos.window.y);
    },
    [visible, isScrollAtBottom, rightDistance],
    null,
    50
  );

  /**
   * Get the position of the element.
   * @param {element} element
   * @returns top and left positions of element
   */
  function getElementPosition(element) {
    const elementOffset = offset(element);
    const elementTop = elementOffset.top;
    const elementLeft = elementOffset.left;
    return { elementTop, elementLeft };
  }

  /**
   * Hook that returns boolean based on users prefers-reduced-motion media query setting
   * @param {boolean} defaultVal - default set to true, no animation
   * @param {string} listener - specify listener or defaults to "change"
   */
  // Show animations as default
  const reducedMotion = useReducedMotion(false);

  /**
   * Scroll to the top of the containing element
   * @param {*} targetPosition - the target position to scroll to, 0 = top
   * @param {*} duration - scroll duration in milliseconds; default is "250"
   * @param {*} element - the html element that should be scrolled; default is the main document element
   * @param {*} callback - run callback when scrolling ends
   */

  const scrollToTop = (
    targetPosition = 0,
    duration,
    element = document.documentElement,
    callback
  ) => {
    if (typeof window === "undefined") return;

    function noAnimation() {
      window.scroll(0, 0);
      callback?.();
      return undefined;
    }

    const request = reducedMotion
      ? noAnimation()
      : window.requestAnimationFrame ||
        window.msRequestAnimationFrame ||
        window.mozRequestAnimationFrame ||
        window.webkitRequestAnimationFrame;

    let distance = element.scrollTop;
    const cosParameter = (distance - targetPosition) / 2;
    let increment = 0;
    let oldTimestamp = null;

    function step(newTimestamp) {
      if (oldTimestamp !== null) {
        increment +=
          (Math.PI * (newTimestamp - oldTimestamp)) / parseInt(duration);

        // if greater than PI, set to page top
        if (increment >= Math.PI) {
          element.scrollTop = targetPosition;
          if (callback) {
            callback();
          }
          return;
        }

        // increments on the scroll is determined by duration / angle(cosign)
        // when the duration is at halfway point, the pixel difference will be at its maxiumum
        // cosign determines how far to raise the scroll in pixels
        const easeInOut =
          cosParameter + targetPosition + cosParameter * Math.cos(increment);
        element.scrollTop = easeInOut;
      }

      oldTimestamp = newTimestamp;
      request?.(step);
    }
    request?.(step);
  };

  /**
   * When scroll top button is clicked execute scrolling functionality
   * and execute click prop if available.
   */
  const onClickHandler = () => {
    onClick?.();
    duration && scrollToTop(0, duration, document.documentElement, onScrollEnd);
  };

  return (
    <button
      type="button"
      ref={scrollToTopRef}
      id={id}
      className={classNames(
        "spark-scroll-to-top",
        { visible: visible, "at-bottom": isScrollAtBottom },
        className
      )}
      style={{ right: rightDistance.right }}
      aria-label={ariaLabel}
      onClick={onClickHandler}
      data-testid={testId}
    >
      <i className="spark-scroll-to-top__icon"></i>
    </button>
  );
}

// Default speed is set to 250ms if not specified
// Backwards compatibility to support component property. Enum import should be used instead.
ScrollToTop.SCROLL = ScrollToTopSpeed;

ScrollToTop.propTypes = {
  /** For scroll to top accessibility */
  ariaLabel: string,

  /** Any extra CSS classes for the component */
  className: string,

  /** Options to set the scroll speed to `FAST` or `SLOW` in milliseconds. Defaults to FAST. */
  duration: oneOf(Object.values(ScrollToTopSpeed)),

  /** ID for the component */
  id: string,

  /** A callback that runs when the button is clicked */
  onClick: func,

  /** A callback that runs after the button is clicked and page has scrolled to the top */
  onScrollEnd: func,

  /** Testing Key for Jest */
  testId: string,
};

ScrollToTop.displayName = "ScrollToTop";
